import { css, cva } from '@styled-system/css';
import { Clock, Euro, Percent, PiggyBank, TrendingUp } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

import type { AnalysisStatisticsData } from '@/features/analysisPageStatistics/types';
import {
  computeNicheScore,
  estimateMonthlyRevenue,
  formatRevenue,
  getNicheScoreTier,
  type NicheScoreTier,
} from '@/shared/utils/analysis/nicheScore';
import { formatNumber, formatPercentage, formatPrice, formatTime } from '@/shared/utils/format';

// ============================================================================
// Bandeau des métriques clés — score de niche /100 en anneau + tuiles KPI.
// Props-driven, partagé produit/vendeur.
// ============================================================================

// Une seule ligne de 6 tuiles (score + 5 stats) — passe en 2 colonnes sur
// écran étroit.
const grid = cva({
  base: {
    display: 'grid',
    gap: '4',
    '@media (max-width: 1100px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    '@media (max-width: 640px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  variants: {
    columns: {
      6: { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
      5: { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
    },
  },
  defaultVariants: { columns: 6 },
});

// Même habillage que les tuiles de l'onglet Statistiques (AnalysisStatsPanel) :
// surface.subtle, bordure, icône inline avec le label.
const tile = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  minWidth: 0,
  justifyContent: 'center',
});

const tileHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'caption',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const tileBody = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
});

const tileLabel = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const tileValue = css({
  fontFamily: 'mono',
  fontSize: '18px',
  lineHeight: '1.15',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.5px',
  whiteSpace: 'nowrap',
});

// ─── Tuile score — anneau de progression coloré par tier ───────────────────

const scoreTile = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  minWidth: 0,
});

const tierLabel = cva({
  base: {
    fontSize: 'bodySm',
    fontWeight: 'semibold',
  },
  variants: {
    tier: {
      poor: { color: 'error.default' },
      average: { color: 'warning.default' },
      good: { color: 'success.default' },
    },
  },
});

const TIER_COLOR_VAR: Record<NicheScoreTier, string> = {
  poor: 'var(--colors-error-default)',
  average: 'var(--colors-warning-default)',
  good: 'var(--colors-success-default)',
};

const TIER_SOFT_VAR: Record<NicheScoreTier, string> = {
  poor: 'var(--colors-error-soft)',
  average: 'var(--colors-warning-soft)',
  good: 'var(--colors-success-soft)',
};

// Anneau de score — chiffre centré à l'intérieur, disque soft en fond,
// arc de progression épais aux bouts arrondis.
const ScoreRing: FC<{ score: number | null; tier: NicheScoreTier }> = ({ score, tier }) => {
  const size = 44;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score == null ? 0 : Math.max(0, Math.min(score, 100)) / 100;

  return (
    <div className={css({ position: 'relative', flexShrink: 0 })} style={{ width: size, height: size }}>
      <svg width={size} height={size} role="img" aria-label={`${score ?? '—'}/100`}>
        <circle cx={size / 2} cy={size / 2} r={radius - stroke / 2 - 1} fill={TIER_SOFT_VAR[tier]} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--colors-surface-sunken)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={TIER_COLOR_VAR[tier]}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 400ms ease' }}
        />
      </svg>
      <span
        className={css({
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'mono',
          fontSize: 'bodySm',
          fontWeight: 'bold',
          letterSpacing: '-0.4px',
        })}
        style={{ color: TIER_COLOR_VAR[tier] }}
      >
        {score ?? '—'}
      </span>
    </div>
  );
};

// ============================================================================

export interface AnalysisKeyStatsProps {
  data: AnalysisStatisticsData | undefined;
  loading?: boolean;
  /** false : pas de tuile score (analyses vendeur — le score est une notion de niche). */
  showScore?: boolean;
}

export const AnalysisKeyStats: FC<AnalysisKeyStatsProps> = ({ data, loading, showScore = true }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language ?? 'fr';

  const score = computeNicheScore(data);
  const tier = getNicheScoreTier(score);
  const revenue = estimateMonthlyRevenue(data);

  const tiles: { key: string; icon: ReactNode; label: string; value: string }[] = [
    { key: 'revenue', icon: <PiggyBank size={16} strokeWidth={1.75} />, label: t('keyStats.monthlyRevenue'), value: formatRevenue(revenue, lang) },
    { key: 'salePct', icon: <Percent size={16} strokeWidth={1.75} />, label: t('keyStats.salePct'), value: formatPercentage(data?.salePercentage ?? null) },
    { key: 'saleTime', icon: <Clock size={16} strokeWidth={1.75} />, label: t('keyStats.saleTime'), value: formatTime(data?.averageSaleTimeSec ?? null, 'min') },
    { key: 'avgPrice', icon: <Euro size={16} strokeWidth={1.75} />, label: t('keyStats.avgPrice'), value: formatPrice(data?.averageSalePrice ?? null) },
    { key: 'salesPerDay', icon: <TrendingUp size={16} strokeWidth={1.75} />, label: t('keyStats.salesPerDay'), value: formatNumber(data?.salesPerDay ?? null) },
  ];

  const TIER_LABEL_KEYS: Record<string, string> = {
    poor: 'keyStats.tierPoor',
    average: 'keyStats.tierAverage',
    good: 'keyStats.tierGood',
  };

  return (
    <div className={grid({ columns: showScore ? 6 : 5 })}>
      {/* Score de la niche — tuile héro avec anneau */}
      {showScore && (
        <div className={scoreTile}>
          <ScoreRing score={loading ? null : score} tier={tier} />
          <div className={tileBody}>
            <span className={tileLabel}>{t('keyStats.nicheScore')}</span>
            {loading ? (
              <Skeleton width={56} />
            ) : (
              <span className={tierLabel({ tier })}>{score == null ? '—' : t(TIER_LABEL_KEYS[tier])}</span>
            )}
          </div>
        </div>
      )}

      {tiles.map((stat) => (
        <div key={stat.key} className={tile}>
          <span className={tileHeader}>
            {stat.icon}
            {stat.label}
          </span>
          <span className={tileValue}>{loading ? <Skeleton width={56} /> : stat.value}</span>
        </div>
      ))}
    </div>
  );
};
